/**
 * My Account — sign-in email change, OTP-verified.
 *
 * The code goes to the NEW address, because the thing being proved is control
 * of the new inbox. The OLD address gets a security NOTICE after the change
 * lands — that notice is the account-takeover control (which is why other
 * sessions are deliberately not invalidated), so if it fails to send we say so
 * plainly here and the server pages an operator.
 */
import { useState } from "react";
import { Field, GhostButton, PrimaryButton, inputClass } from "@/components/consumer/chrome";
import { confirmEmailChange, requestEmailChange } from "@/services/account-email";
import { OTP_LENGTH } from "@/lib/otp";

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-muted">
    {children}
  </span>
);

/**
 * Real server codes mapped to what the person can DO about them. Anything that
 * a retry cannot fix must NOT invite a retry — that was the defect this screen
 * shared with the age-gate message, which hid AGE_AFFIRMATION_REQUIRED behind
 * "try again".
 */
const MESSAGES: Record<string, string> = {
  invalid_email: "That doesn't look like a valid email address.",
  same_email: "That's already your sign-in email.",
  email_unavailable: "That address is already in use on another account. Try a different one.",
  rate_limited: "Too many attempts. Try again in an hour.",
  session_expired: "Your sign-in has expired. Sign in again, then change your email.",
  send_failed:
    "Something on our side stopped the code being sent, so trying again won't help. Contact support and we'll change it for you.",
  request_failed:
    "Something on our side stopped the code being sent, so trying again won't help. Contact support and we'll change it for you.",
  unexpected:
    "Something on our side went wrong. Contact support and we'll change it for you.",
};

const FALLBACK_MESSAGE =
  "We couldn't start the change, and it isn't something you can fix by trying again. Contact support and we'll change it for you.";


type Stage = "idle" | "entering" | "verifying" | "done";

export default function EmailChangeSection({
  email,
  onChanged,
}: {
  email: string | null;
  onChanged?: (newEmail: string) => void;
}) {
  const [stage, setStage] = useState<Stage>("idle");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noticeWarning, setNoticeWarning] = useState(false);

  function reset() {
    setStage("idle");
    setNewEmail("");
    setCode("");
    setError(null);
    setBusy(false);
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    const res = await requestEmailChange(newEmail.trim().toLowerCase());
    setBusy(false);
    if (!res.ok) {
      setError(MESSAGES[res.error ?? ""] ?? FALLBACK_MESSAGE);
      return;
    }
    setStage("verifying");
  }

  async function verify() {
    if (!email) return;
    setBusy(true);
    setError(null);
    const target = newEmail.trim().toLowerCase();
    const res = await confirmEmailChange(target, code, email);
    setBusy(false);
    if (!res.ok) {
      setError("That code isn't valid or has expired. Request a new one.");
      return;
    }
    setNoticeWarning(res.noticeSent === false);
    setStage("done");
    onChanged?.(target);
  }

  if (stage === "idle" || stage === "done") {
    return (
      <div className="mt-3.5" data-testid="s9-email-change">
        <div className="flex flex-col gap-[3px]">
          <FieldLabel>Sign-in email</FieldLabel>
          <span className="text-[16px] font-medium" data-testid="s9-signin-email">
            {email ?? "—"}
          </span>
        </div>
        <div className="mt-3.5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setStage("entering")}
            disabled={!email}
            className="min-h-11 rounded-xl border border-foreground/15 px-3.5 text-[14px] font-semibold text-foreground disabled:opacity-60"
            data-testid="s9-change-email"
          >
            Change email
          </button>
          {stage === "done" ? (
            <span className="text-[14px] font-semibold text-[#1A6B42]">Email updated</span>
          ) : null}
        </div>
        {stage === "done" && noticeWarning ? (
          <p className="mt-2 text-[14px] font-semibold text-[#C41112]" data-testid="s9-notice-failed">
            Your email was changed, but we couldn't deliver the security notice to your previous
            address. Our team has been alerted.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-3.5 flex flex-col gap-3.5" data-testid="s9-email-change-form">
      {stage === "entering" ? (
        <>
          <Field label="New sign-in email">
            <input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className={inputClass}
              type="email"
              autoComplete="email"
              inputMode="email"
              data-testid="s9-new-email"
            />
          </Field>
          <p className="text-[14px] text-slate-muted">
            We'll email an {OTP_LENGTH}-digit code to the new address to confirm you can receive mail there.
            Your previous address gets a security notice once the change is made.
          </p>
        </>
      ) : (
        <>
          <Field label={`Code sent to ${newEmail}`}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
              className={inputClass}
              inputMode="numeric"
              autoComplete="one-time-code"
              data-testid="s9-email-code"
            />
          </Field>
          <p className="text-[14px] text-slate-muted">
            Enter the code from the message we sent to your new address.
          </p>
        </>
      )}

      {error ? (
        <p className="text-[14px] font-semibold text-[#C41112]" data-testid="s9-email-error">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <PrimaryButton
          onClick={stage === "entering" ? sendCode : verify}
          disabled={busy || (stage === "entering" ? newEmail.trim().length < 5 : code.length < OTP_LENGTH)}
          data-testid="s9-email-submit"
        >
          {busy ? "Working…" : stage === "entering" ? "Send code" : "Confirm change"}
        </PrimaryButton>
        <GhostButton onClick={reset} data-testid="s9-email-cancel">
          Cancel
        </GhostButton>
      </div>
    </div>
  );
}
